import { FolderOpen } from '@phosphor-icons/react';
import type { FC, ReactNode } from 'react';

interface EmptyStateProps {
  /** What is missing, as a noun phrase — not "No results found". */
  title: string;
  /** One sentence on why it is empty, or on what to do about it. */
  message: string;
  /** A Phosphor icon element, sized ~28. Defaults to `FolderOpen`. */
  icon?: ReactNode;
  /** The way out — usually the same button the toolbar offers. */
  action?: ReactNode;
}

/**
 * The empty state, shown inside the card the content would have filled.
 *
 * An empty panel is not an error, it is a place that has not been filled yet — so it names what
 * is missing and offers the way to fix it, rather than reporting "no data". The icon sits in a
 * tile rather than floating, which keeps it from reading as a warning symbol.
 */
export const EmptyState: FC<EmptyStateProps> = ({ title, message, icon, action }) => (
  <div className="flex flex-col items-center gap-4 px-6 py-16 text-center">
    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
      {icon ?? <FolderOpen size={28} aria-hidden="true" />}
    </span>
    <span className="flex flex-col gap-2">
      <span className="block text-lg font-semibold text-gray-900">{title}</span>
      <span className="block max-w-[400px] text-sm text-gray-500 [text-wrap:pretty]">{message}</span>
    </span>
    {action ? <div className="flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
  </div>
);
